/** Copyright (c) 2020 T. Rowe Price. All rights reserved. */

import './SelectViewableAccounts.scss';

import isEmpty from 'lodash/isEmpty';
import { bool, func, object } from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import { getGrantViewAccessAccounts } from 'state/grantViewAccess/GrantViewAccessSelectors';
import {
  getCustomer,
  getPrimaryEmailAddress
} from 'state/customer/CustomerSelectors';
import SaveLoadingButtonWithPermissions from 'components/shared/LoadingButton/SaveLoadingButtonWithPermissions';
import { GroupedAccountList } from 'components/shared/GroupedAccountList/GroupedAccountList';
import { GroupedAccountListHeader } from 'components/shared/GroupedAccountList/GroupedAccountListHeader';
import { AccountList } from 'components/shared/GroupedAccountList/AccountList';
import { AccountListItem } from 'components/shared/GroupedAccountList/AccountListItem';
import { getAccountHeaderClassname } from 'components/shared/GroupedAccountList/util';
import AccountDisplay from 'components/shared/AccountDisplay/AccountDisplay';
import { addAccountViewAccess } from 'services/GrantViewAccess';
import { getView } from 'state/view/ViewSelectors';
import GvaCheckbox from 'components/shared/GvaCheckbox/GvaCheckbox';

const SelectViewableAccounts = ({
  accountGroups,
  checkedItems,
  customer: user,
  customerEmail,
  grantee,
  onBack = () => {},
  onClose = () => {},
  onSave = () => {},
  shouldDisableSave,
  updateCheckedItems,
  view
}) => {
  const updateCheckedItem = (name) => (isChecked) =>
    updateCheckedItems({
      ...checkedItems,
      [name]: isChecked
    });

  const handleSaveClick = () => {
    addAccountViewAccess(
      grantee,
      checkedItems,
      onSave,
      customerEmail,
      user,
      view
    );
  };

  return (
    <Fragment>
      <div className="gva-add-accounts">
        <div className="account-list-item-type-list">
          {Object.entries(accountGroups).map(
            ([accountGroupName, accounts], i) => (
              <GroupedAccountList
                key={i}
                groupClassification={getAccountHeaderClassname(accounts)}>
                <GroupedAccountListHeader accountGroupName={accountGroupName} />
                <AccountList>
                  {accounts.map((account) => (
                    <AccountListItem key={account.accountId}>
                      <AccountDisplay
                        account={account}
                        withLink={false}
                        withOwners
                      />
                      <GvaCheckbox
                        label={account.accountName}
                        accountId={account.accountId}
                        checked={checkedItems[account.accountId]}
                        name={account.accountWChecksum}
                        onChange={updateCheckedItem(account.accountId)}
                      />
                    </AccountListItem>
                  ))}
                </AccountList>
              </GroupedAccountList>
            )
          )}
        </div>
        <div className="entity-disclaimer">
          Note: Entities (corporations) are excluded from granting and receiving
          View Access.
        </div>
      </div>
      {isEmpty(checkedItems) && (
        <p className="account-select-requirement-message">
          You need to select at least one account for access in order to save
          changes.
        </p>
      )}
      <div className="new-user-modal-button-group">
        <button
          className="button new-user-modal-back"
          id="nav-back"
          onClick={onBack}>
          Back
        </button>
        <button
          className="button cancel-button"
          id="cancel-select-accounts"
          onClick={onClose}>
          Cancel
        </button>
        <SaveLoadingButtonWithPermissions
          className="button new-user-modal-save"
          id="save-account-selection"
          disabled={shouldDisableSave}
          onClick={handleSaveClick}
          requestStatusKey="updateViewRelationships"
          shouldDisable>
          Save
        </SaveLoadingButtonWithPermissions>
      </div>
    </Fragment>
  );
};

SelectViewableAccounts.propTypes = {
  accountGroups: object,
  checkedItems: object,
  grantee: object,
  onBack: func,
  onClose: func,
  onSave: func,
  shouldDisableSave: bool,
  updateFormField: func
};

const mapStateToProps = (state) => ({
  accountGroups: getGrantViewAccessAccounts(state),
  customer: getCustomer(state),
  customerEmail: getPrimaryEmailAddress(state).address || '',
  view: getView(state)
});

export default connect(mapStateToProps)(SelectViewableAccounts);
